/**
 * OPTIONS_TO_EQUIPMENT Bridge
 * Transforms Settings Tab toggles into concrete equipment items
 * This is the core of Phase 1 implementation - bridging vessel settings to equipment register
 */

/**
 * Equipment templates for generating concrete equipment from settings toggles
 * These templates define how vessel configuration choices become actual equipment items
 */
export const EQUIPMENT_TEMPLATES = {
  // Life Saving Appliances
  MOB_Recovery_Device: {
    domain: 'LSA',
    category: 'LSA',
    name: 'MOB Recovery Device',
    description: 'Man overboard recovery equipment',
    qtyRequired: 0, // Optional equipment
    reference: 'WBC3 15.3'
  },
  
  Additional_Lifebuoy: {
    domain: 'LSA', 
    category: 'LSA',
    name: 'Additional Lifebuoy',
    description: 'Additional lifebuoy beyond minimum requirement',
    qtyRequired: 0,
    reference: 'WBC3 15.2'
  },

  Liferaft_6_Person: {
    domain: 'LSA',
    category: 'LSA', 
    name: 'Liferaft (6 person)',
    description: 'Inflatable liferaft for 6 persons',
    qtyRequired: 0,
    reference: 'WBC3 15.4'
  },

  // Fire Fighting Equipment
  Fixed_Fire_System: {
    domain: 'FFE',
    category: 'FFE',
    name: 'Fixed Fire Extinguishing System',
    description: 'Fixed fire suppression system',
    qtyRequired: 0,
    reference: 'WBC3 16.4'
  },

  Fire_Blanket: {
    domain: 'FFE',
    category: 'FFE', 
    name: 'Fire Blanket',
    description: 'Fire blanket for galley/open flame protection',
    qtyRequired: 0,
    reference: 'WBC3 16.3.1'
  },

  Additional_Fire_Extinguisher: {
    domain: 'FFE',
    category: 'FFE',
    name: 'Additional Fire Extinguisher',
    description: 'Additional fire extinguisher beyond minimum requirement',
    qtyRequired: 0,
    reference: 'WBC3 16.3'
  },

  // Navigation Equipment
  ECDIS_Console: {
    domain: 'NAV',
    category: 'NAV',
    name: 'ECDIS Console',
    description: 'Electronic Chart Display and Information System',
    qtyRequired: 0,
    reference: 'WBC3 20.4'
  },

  Radar_System: {
    domain: 'NAV',
    category: 'NAV',
    name: 'Radar System',
    description: 'Marine radar for navigation',
    qtyRequired: 0,
    reference: 'WBC3 20.3'
  },

  AIS_Transponder: {
    domain: 'NAV',
    category: 'NAV',
    name: 'AIS Transponder',
    description: 'Automatic Identification System',
    qtyRequired: 0,
    reference: 'WBC3 20.6'
  },

  // GMDSS Equipment
  NAVTEX_Receiver: {
    domain: 'GMDSS',
    category: 'GMDSS',
    name: 'NAVTEX Receiver', 
    description: 'Navigational Telex receiver for weather and safety information',
    qtyRequired: 0,
    reference: 'WBC3 21.3'
  },

  MF_Radio: {
    domain: 'GMDSS',
    category: 'GMDSS',
    name: 'MF/HF Radio',
    description: 'Medium/High Frequency radio',
    qtyRequired: 0,
    reference: 'WBC3 21.2'
  },

  // Machinery Equipment  
  Bow_Thruster: {
    domain: 'MACH',
    category: 'MACH',
    name: 'Bow Thruster',
    description: 'Bow thruster for enhanced maneuverability',
    qtyRequired: 0,
    reference: 'Good seamanship'
  },

  // Electrical Equipment
  General_Alarm: {
    domain: 'ELEC',
    category: 'ELEC',
    name: 'General Alarm System',
    description: 'General alarm for emergency situations',
    qtyRequired: 0,
    reference: 'WBC3 18.2'
  },

  PA_System: {
    domain: 'ELEC',
    category: 'ELEC', 
    name: 'Public Address System',
    description: 'PA system for announcements',
    qtyRequired: 0,
    reference: 'WBC3 18.3'
  },

  Emergency_Lighting: {
    domain: 'ELEC',
    category: 'ELEC',
    name: 'Emergency Lighting',
    description: 'Emergency lighting system',
    qtyRequired: 0,
    reference: 'WBC3 18.1'
  },

  // Operational Equipment
  Night_Vision_Equipment: {
    domain: 'operations',
    category: 'Operations Equipment',
    name: 'Night Vision Equipment',
    description: 'Thermal imaging/night vision for enhanced navigation',
    qtyRequired: 0,
    reference: 'Optional operational enhancement'
  },

  Internet_WiFi_System: {
    domain: 'operations',
    category: 'Operations Equipment',
    name: 'Internet/WiFi System',
    description: 'Internet connectivity for weather/charts/communication',
    qtyRequired: 0,
    reference: 'Optional operational enhancement'
  }
};

/**
 * Mapping from vessel configuration paths to equipment templates
 * This is the bridge that transforms settings toggles to concrete equipment
 */
export const OPTIONS_TO_EQUIPMENT_MAPPING = {
  // LSA Equipment from vessel optional equipment array
  'optionalEquipment.liferaft': [{
    template: 'Liferaft_6_Person',
    defaults: { subtype: '6_person', qtyPresent: 1 }
  }],
  
  'optionalEquipment.additional_lifebuoys': [{
    template: 'Additional_Lifebuoy', 
    defaults: { qtyPresent: 1 }
  }],

  // MOB Equipment - Abstract choice handled separately
  // This would come from abstract requirements system

  // Fire Fighting Equipment
  'optionalEquipment.fire_blankets': [{
    template: 'Fire_Blanket',
    defaults: { qtyPresent: 1 }
  }],

  'optionalEquipment.additional_extinguishers': [{
    template: 'Additional_Fire_Extinguisher',
    defaults: { qtyPresent: 1 }
  }],

  // Fixed systems based on configuration  
  'fixedFFE.galley': [{
    template: 'Fixed_Fire_System',
    defaults: { subtype: 'galley', qtyPresent: 1 }
  }],

  'fixedFFE.engine': [{
    template: 'Fixed_Fire_System', 
    defaults: { subtype: 'engine', qtyPresent: 1 }
  }],

  'fixedFFE.accommodation': [{
    template: 'Fixed_Fire_System',
    defaults: { subtype: 'accommodation', qtyPresent: 1 }
  }],

  // Navigation Equipment
  'optionalEquipment.ecdis': [{
    template: 'ECDIS_Console',
    defaults: { qtyPresent: 1 }
  }],

  'optionalEquipment.radar': [{
    template: 'Radar_System', 
    defaults: { qtyPresent: 1 }
  }],

  'optionalEquipment.ais': [{
    template: 'AIS_Transponder',
    defaults: { qtyPresent: 1 }
  }],

  // GMDSS Equipment
  'optionalEquipment.navtex': [{
    template: 'NAVTEX_Receiver',
    defaults: { qtyPresent: 1 }
  }],

  'optionalEquipment.mf_radio': [{
    template: 'MF_Radio',
    defaults: { qtyPresent: 1 }
  }],

  // Machinery Equipment
  'optionalEquipment.bow_thruster': [{
    template: 'Bow_Thruster',
    defaults: { qtyPresent: 1 }
  }],

  // Electrical Equipment  
  'optionalEquipment.general_alarm': [{
    template: 'General_Alarm',
    defaults: { qtyPresent: 1 }
  }],

  'optionalEquipment.pa_system': [{
    template: 'PA_System',
    defaults: { qtyPresent: 1 }
  }],

  'optionalEquipment.emergency_lighting': [{
    template: 'Emergency_Lighting',
    defaults: { qtyPresent: 1 }
  }],

  // Operational Equipment
  'operationalEquipment.night_vision': [{
    template: 'Night_Vision_Equipment',
    defaults: { qtyPresent: 1 }
  }],

  'operationalEquipment.internet': [{
    template: 'Internet_WiFi_System',
    defaults: { qtyPresent: 1 }
  }]
};

/**
 * Get equipment items from vessel configuration using OPTIONS_TO_EQUIPMENT bridge
 * @param {Object} vesselConfig - Vessel configuration object
 * @returns {Array} Array of equipment items derived from settings
 */
export function getEquipmentFromVesselOptions(vesselConfig) {
  const equipmentItems = [];
  
  if (!vesselConfig) {
    return equipmentItems;
  }

  // Process all mappings
  Object.keys(OPTIONS_TO_EQUIPMENT_MAPPING).forEach(configPath => {
    const value = getNestedValue(vesselConfig, configPath);
    
    // Check if this option is enabled
    if (isOptionEnabled(configPath, value, vesselConfig)) {
      const mappings = OPTIONS_TO_EQUIPMENT_MAPPING[configPath];
      
      mappings.forEach(mapping => {
        const template = EQUIPMENT_TEMPLATES[mapping.template];
        if (template) {
          const equipment = createEquipmentFromTemplate(template, mapping.defaults, configPath);
          equipmentItems.push(equipment);
        }
      });
    }
  });

  return equipmentItems;
}

/**
 * Get nested value from object using dot notation
 * @param {Object} obj - Object to search
 * @param {string} path - Dot notation path (e.g., 'fixedFFE.galley')
 * @returns {any} Value at path or undefined
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Check if an option is enabled based on the config path and value
 * @param {string} configPath - Configuration path
 * @param {any} value - Value at the path  
 * @param {Object} vesselConfig - Full vessel config for context
 * @returns {boolean} True if option is enabled
 */
function isOptionEnabled(configPath, value, vesselConfig) {
  // For optionalEquipment array items
  if (configPath.startsWith('optionalEquipment.')) {
    const equipmentId = configPath.replace('optionalEquipment.', '');
    const optionalEquipment = vesselConfig.optionalEquipment || [];
    return Array.isArray(optionalEquipment) && optionalEquipment.includes(equipmentId);
  }
  
  // For operationalEquipment array items (Step 6 selections)
  if (configPath.startsWith('operationalEquipment.')) {
    const equipmentId = configPath.replace('operationalEquipment.', '');
    const operationalEquipment = vesselConfig.operationalEquipment || [];
    return Array.isArray(operationalEquipment) && operationalEquipment.includes(equipmentId);
  }
  
  // For boolean flags (fixedFFE, etc.)
  return Boolean(value);
}

/**
 * Create equipment item from template with defaults applied
 * @param {Object} template - Equipment template
 * @param {Object} defaults - Default values to apply
 * @param {string} sourcePath - Configuration path this came from
 * @returns {Object} Equipment item
 */
function createEquipmentFromTemplate(template, defaults, sourcePath) {
  // Generate deterministic ID
  const equipmentId = `opt_${template.domain.toLowerCase()}_${sourcePath.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`;
  
  return {
    id: equipmentId,
    name: template.name,
    domain: template.domain,
    category: template.category,
    source: 'options', // Equipment came from vessel settings options
    presence: 'present', // Assume present since it's selected in settings
    qtyRequired: template.qtyRequired,
    qtyPresent: defaults.qtyPresent || 1,
    subtype: defaults.subtype,
    reference: template.reference,
    description: template.description,
    selected: true, // Pre-selected since it's enabled in settings
    selectable: true, // Can be deselected in Equipment Selection Tab
    sourceConfigPath: sourcePath, // Track where this came from
    ...defaults // Apply any additional defaults
  };
}

/**
 * Get all possible equipment options that could be enabled
 * Used for discovering available equipment in ExtrasDrawer
 * @param {string} domain - Equipment domain to filter by
 * @returns {Array} Array of all possible equipment templates for domain
 */
export function getAvailableEquipmentForDomain(domain) {
  return Object.keys(EQUIPMENT_TEMPLATES)
    .map(templateId => EQUIPMENT_TEMPLATES[templateId])
    .filter(template => template.domain === domain)
    .map(template => ({
      ...template,
      id: `template_${template.domain.toLowerCase()}_${template.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`,
      source: 'template',
      presence: 'missing',
      qtyRequired: 0, // All template items are optional additions
      qtyPresent: 0,
      selected: false,
      selectable: true
    }));
}

/**
 * Debug function to show what equipment would be generated from vessel config
 * @param {Object} vesselConfig - Vessel configuration
 * @returns {Object} Debug information
 */
export function debugOptionsToEquipment(vesselConfig) {
  const debug = {
    vesselConfig: vesselConfig,
    enabledPaths: [],
    generatedEquipment: [],
    availableTemplates: Object.keys(EQUIPMENT_TEMPLATES).length
  };

  // Check which paths are enabled
  Object.keys(OPTIONS_TO_EQUIPMENT_MAPPING).forEach(configPath => {
    const value = getNestedValue(vesselConfig, configPath);
    const enabled = isOptionEnabled(configPath, value, vesselConfig);
    
    debug.enabledPaths.push({
      path: configPath,
      value: value,
      enabled: enabled
    });
  });

  // Generate equipment
  debug.generatedEquipment = getEquipmentFromVesselOptions(vesselConfig);

  return debug;
}